import React, { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, TextRun } from "docx";
import { Box, CircularProgress, Typography }
    from "@mui/material";
import dayjs from "dayjs";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import PatientFormDialog from "../shared/PatientFormDialog";
import { DateRangeChangeDirection, SeizureTypeReportRangeType } from "./enums";
import AccessLocalStorage from "../../services/localStorage";
import { getApiUrl } from "../../services/api_service";
import { dashboardText, mainGrid } from "../../style2";
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
//import { useStyles } from "../../style";
import { useSelector } from "react-redux";
import { calculateAge } from "../shared/dashboard/detail_text";

export function SeizureReportQOL(props) {
    const patient = useSelector((state) => state.patient.value);
    // DOCX Export Handler
    const handleDownloadDocx = () => {
        if (!reportData || reportData.length === 0) return;
        // Build header row
        const headerRow = [
            new DocxTableCell({ children: [new Paragraph("")] }),
            ...reportData.map(point => new DocxTableCell({ children: [new Paragraph(dayjs(point.answerSubmitDate).format('DD/MM/YY'))] }))
        ];

        // Helper to make row
        const makeRow = (label, accessor, formatFn) =>
            new DocxTableRow({
                children: [
                    new DocxTableCell({ children: [new Paragraph(label)] }),
                    ...reportData.map((point) => new DocxTableCell({
                        children: [
                            new Paragraph(
                                formatFn ? formatFn(point[accessor], point) : (point[accessor] == null ? "N/A" : String(point[accessor]))
                            )
                        ]
                    }))
                ]
            });

        // Table cell padding for all cells
        const cellPadding = { top: 50, bottom: 50, left: 50, right: 50 };

        // Helper to make row with cell padding
        const makeRowWithPadding = (label, accessor, formatFn) =>
            new DocxTableRow({
                children: [
                    new DocxTableCell({
                        children: [new Paragraph({ text: label, alignment: "center" })],
                        margins: cellPadding,
                        verticalAlign: "center"
                    }),
                    ...reportData.map((point) => new DocxTableCell({
                        children: [
                            new Paragraph({
                                text: formatFn ? formatFn(point[accessor], point) : (point[accessor] == null ? "N/A" : String(point[accessor])),
                                alignment: "center"
                            })
                        ],
                        margins: cellPadding,
                        verticalAlign: "center"
                    }))
                ]
            });

        // Header row with padding
        const headerRowWithPadding = new DocxTableRow({
            children: [
                new DocxTableCell({ children: [new Paragraph("")], margins: { top: 80, bottom: 80, left: 80, right: 80 } }),
                ...reportData.map(point => new DocxTableCell({ children: [new Paragraph(dayjs(point.answerSubmitDate).format('DD/MM/YY'))], margins: { top: 80, bottom: 80, left: 80, right: 80 } }))
            ]
        });

        // Table rows with padding
        const rows = [
            headerRowWithPadding,
            makeRowWithPadding("Able to attend school?", "attendSchool", (val, point) => {
                if (val == null) return "N/A";
                let txt = val;
                if (val === "Yes" && point.daysAttended != null) txt += ` (${point.daysAttended}/5 days)`;
                return txt;
            }),
            makeRowWithPadding("Participate in family life?", "participateFamilyLife"),
            makeRowWithPadding("Enjoy family activities?", "enjoyFamilyActivities"),
            makeRowWithPadding("Overall Week", "overAllWeekScore"),
            makeRowWithPadding("Seizures", "seizures"),
            makeRowWithPadding("Alertness", "alertness"),
            makeRowWithPadding("Interaction", "interaction"),
            makeRowWithPadding("Memory & Concentration", "memoryAndConcentration"),
            makeRowWithPadding("Understanding & Communication", "understanding"),
            makeRowWithPadding("Behaviour", "behaviour"),
            makeRowWithPadding("Sleep", "sleep"),
            makeRowWithPadding("General Wellbeing", "generalWellbeing"),
            makeRowWithPadding("Family Wellbeing", "familyWellbeing"),
            makeRowWithPadding("Other Contributing factors", "factorsAffectingWeek", (val) => (val && val.length > 0 ? val.join(", ") : "Not Answered")),
        ];

        // Child details as a table
        const childDetailsHeading = new Paragraph({
            text: "Child/Young Person Details",
            heading: "Heading2",
            spacing: { after: 100 },
            style: "CustomHeading2"
        });

        const childDetailsTable = new DocxTable({
            rows: [
                new DocxTableRow({
                    children: [
                        new DocxTableCell({ children: [new Paragraph({ text: "Child's Name", style: "BodyText" })], width: { size: 30, type: "pct" }, margins: cellPadding }),
                        new DocxTableCell({ children: [new Paragraph({ text: `${patient.user.firstName} ${patient.user.surname}`, style: "BodyText" })], width: { size: 70, type: "pct" }, margins: cellPadding })
                    ]
                }),
                new DocxTableRow({
                    children: [
                        new DocxTableCell({ children: [new Paragraph({ text: "Date of Birth", style: "BodyText" })], width: { size: 30, type: "pct" }, margins: cellPadding }),
                        new DocxTableCell({ children: [new Paragraph({ text: `${dayjs(patient.user.dob).format('DD/MM/YY')} (${calculateAge(patient.user.dob)})`, style: "BodyText" })], width: { size: 70, type: "pct" }, margins: cellPadding })
                    ]
                }),
                new DocxTableRow({
                    children: [
                        new DocxTableCell({ children: [new Paragraph({ text: "NHS Number", style: "BodyText" })], width: { size: 30, type: "pct" }, margins: cellPadding }),
                        new DocxTableCell({ children: [new Paragraph({ text: `${patient.user.nhsNumber}`, style: "BodyText" })], width: { size: 70, type: "pct" }, margins: cellPadding })
                    ]
                })
            ]
        });
        // Add line break after table
        const afterChildTableBreak = new Paragraph({ text: "", spacing: { after: 200 } });

        const qolHeading = new Paragraph({
            text: "Wellbeing & Quality of Life Overview",
            heading: "Heading2",
            style: "CustomHeading2",
            spacing: { after: 100 }
        });

        // Legend row as a single-row table (value/label pairs and N/A/Not Completed)
        const legendItems = [
            { value: "5", label: "Very Good" },
            { value: "4", label: "Good" },
            { value: "3", label: "Neutral" },
            { value: "2", label: "Bad" },
            { value: "1", label: "Very Bad" },
            { value: "N/A", label: "Not Completed" },
        ];
        const legendRow = new DocxTableRow({
            children: legendItems.map(item =>
                new DocxTableCell({
                    children: [
                        new Paragraph({
                            children: [
                                new TextRun({ text: item.value + " - ", bold: true }),
                                new TextRun({ text: item.label })
                            ]
                        })
                    ],
                    margins: { top: 50, bottom: 50, left: 50, right: 50 }
                })
            )
        });
        const legendTable = new DocxTable({
            rows: [legendRow],
            width: { size: 100, type: "pct" },
            borders: {
                top:    { style: "none", size: 0, color: "FFFFFF" },
                bottom: { style: "none", size: 0, color: "FFFFFF" },
                left:   { style: "none", size: 0, color: "FFFFFF" },
                right:  { style: "none", size: 0, color: "FFFFFF" },
                insideHorizontal: { style: "none", size: 0, color: "FFFFFF" },
                insideVertical:   { style: "none", size: 0, color: "FFFFFF" },
            }
        });

        // Add line break after QOL table
        const afterQolTableBreak = new Paragraph({ text: "", spacing: { after: 200 } });

        const doc = new Document({
            styles: {
                default: {
                    document: {
                        run: {
                            font: "Calibri",
                            size: 24 // 12pt
                        },
                    },
                    heading1: {
                        run: {
                            font: "Calibri",
                        },
                    },
                    heading2: {
                        run: {
                            font: "Calibri",
                        },
                    },
                },
                paragraphStyles: [
                    {
                        id: "CustomHeading1",
                        name: "Custom Heading 1",
                        basedOn: "Heading1",
                        next: "Normal",
                        quickFormat: true,
                        run: {
                            size: 40, // 18.5pt
                            bold: true,
                            color: "2F5496", // blue
                        },
                        paragraph: {
                            spacing: { after: 200 },
                        },
                    },
                    {
                        id: "CustomHeading2",
                        name: "Custom Heading 2",
                        basedOn: "Heading2",
                        next: "Normal",
                        quickFormat: true,
                        run: {
                            size: 26, // 13pt
                            bold: true,
                            color: "2F5496", // blue
                        },
                        paragraph: {
                            spacing: { after: 200 },
                        },
                    },
                    {
                        id: "BodyText",
                        name: "Body Text",
                        basedOn: "Normal",
                        next: "Normal",
                        quickFormat: true,
                        run: {
                            size: 24, // 12pt
                        },
                    },
                ],
            },
            sections: [
                {
                    children: [
                        new Paragraph({
                            text: "The Leeds Teaching Hospitals Trust",
                            heading: "CustomHeading1",
                            spacing: { after: 300 }
                        }),
                         new Paragraph({
                            text: "Leeds General Infirmary",
                            heading: "CustomHeading2",
                            spacing: { after: 300 }
                        }),
                         new Paragraph({
                            text: "Department of Paediatric Neurology ",
                            heading: "CustomHeading2",
                            spacing: { after: 400 }
                        }),
                        childDetailsHeading,
                        childDetailsTable,
                        afterChildTableBreak,
                        qolHeading,
                        new Paragraph({
                            text: "This report summarises the parent-reported Quality of Life questionnaire completed at defined intervals during the selected reporting period. It reflects the parent or carer’s perspective on the child’s wellbeing, daily functioning, symptoms, and overall quality of life, and is intended to support longitudinal monitoring, clinical review, and shared decision-making.",
                            style: "BodyText",
                            spacing: { after: 250 },
                        }),
                        new Paragraph({
                            text: "Legend:",
                            style: "BodyText",
                            spacing: { after: 0 },
                        }),
                        legendTable,
                        new Paragraph({
                            text: "",
                            style: "BodyText",
                            spacing: { after: 5 },
                        }),
                        // Defensive: add margins only if row.children exists, else fallback to original rows
                        new DocxTable({ rows }),
                        afterQolTableBreak
                    ]
                }
            ]
        });
        Packer.toBlob(doc).then(blob => {
            saveAs(blob, `${patient.user.firstName} ${patient.user.surname} Epilepsy Quality of Life Summary.docx`);
        });
    };
    const display = useState(SeizureTypeReportRangeType.Monthly);
    const monthOffset = 3;
    const [startDate, setStartDate] = useState(dayjs().subtract(monthOffset, 'month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [reportData, setReportData] = useState(null);
    const [schoolAttendanceSummary, setSchoolAttendanceSummary] = useState([]);
    const [familyParticipationSummary, setFamilyParticipationSummary] = useState([]);
    const [enjoyFamilyActivitiesSummary, setEnjoyFamilyActivitiesSummary] = useState([]);
    const [avgDaysAttended, setAvgDaysAttended] = useState(null);
    const [loadingReportData, setLoadingReportData] = useState(false);
    const [errorReportData, setErrorReportData] = useState('');
    //const classes = useStyles();
    const ls = new AccessLocalStorage();
    const rowBgColor = "#F2FCFB";

    const handleDateRangeChange = (direction) => {

        const start = dayjs(startDate);
        const end = dayjs(endDate);
        if (direction === DateRangeChangeDirection.Left) {
            setStartDate(start.subtract(monthOffset, "month").format("YYYY-MM-DD"));
            setEndDate(end.subtract(monthOffset, "month").format("YYYY-MM-DD"));
        } else if (direction === DateRangeChangeDirection.Right) {
            setStartDate(start.add(monthOffset, "month").format("YYYY-MM-DD"));
            setEndDate(end.add(monthOffset, "month").format("YYYY-MM-DD"));
        }
        else if (direction === DateRangeChangeDirection.Today) {

            setStartDate(dayjs().subtract(monthOffset, 'month').format("YYYY-MM-DD"));
            setEndDate(dayjs().format("YYYY-MM-DD"));
        }
    };

    useEffect(() => {
        const authTokens = localStorage.getItem("authTokens");
        let token;

        if (authTokens) {
            const parsedAuthTokens = JSON.parse(authTokens);
            token = parsedAuthTokens?.accessToken;
        }
        if (!token) {
            return;
        }

        setLoadingReportData(true);
        setErrorReportData('');
        let error = 'Unable to load patient form information.';
        fetch(
            `${getApiUrl()}/seizurediary/reportqol/${ls.getCurrentPatientId()}?startDate=${startDate}&endDate=${endDate}`,
            {
                method: "GET",
                headers: { Authorization: `Bearer ${token}` },
            }
        )
            .then((response) => {
                if (response.status === 200) {
                    response.json().then(data => {
                        setReportData(data.qolList);
                        setSchoolAttendanceSummary(data.schoolAttendanceSummary);
                        setFamilyParticipationSummary(data.familyParticipationSummary);
                        setEnjoyFamilyActivitiesSummary(data.enjoyFamilyActivitiesSummary);
                        setAvgDaysAttended(data.avgDaysAttended);
                        //console.log("Report data:");
                        //console.log(data);
                    }).catch((err) => {
                        setErrorReportData(error);
                        console.log(err);
                    });
                } else {
                    setErrorReportData(error);
                }
            })
            .catch((err) => {
                setErrorReportData(error);
                console.log(err);
            })
            .finally(() => { setLoadingReportData(false); });
    }, [startDate, endDate]);

    const tableHeaderCell = { color: "#26575C", fontWeight: "bold", fontSize: "1.2rem", border: "none" };
    const tableBodyFirstCell = { color: "#26575C", padding: "30px", fontWeight: "bold", fontSize: "1rem", border: "none" };
    const tableBodyCell = { textAlign: "center", color: "#26575C", padding: "30px", fontWeight: "bold", fontSize: "1rem", border: "none", minWidth: "130px", width: display === SeizureTypeReportRangeType.Daily ? "130px" : display === SeizureTypeReportRangeType.Weekly ? "220px" : "275px" };
    const tbg = {
        borderRadius: "50px", // pill shape
        overflow: "hidden",   // ensures rounded corners apply to children
        border: "3px solid #E3E7E8"
    };

    const dateDirBtn = {
        padding: "10px",
        backgroundColor: "#E4F2F2",   // ✅ background
        color: "#26575C",             // ✅ text/icon color
        "&:hover": {
            backgroundColor: "#d0e6e6", // optional hover shade
        }
    };

    function getTableCellWidth(display) {
    /*if (display === SeizureTypeReportRangeType.Daily) {
      return "165px";
    } else if (display === SeizureTypeReportRangeType.Weekly) {
      return "220px";
    } else*/ if (display === SeizureTypeReportRangeType.Monthly) {
            return "130px";
        }
    }

    // Helper to format month/year range
    function getMonthYearRange(startDate, endDate) {
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        return `${start.format("d/MM/YYYY")} - ${end.format("d/MM/YYYY")}`;
        if (start.year() === end.year()) {
            if (start.month() === end.month()) {
                // Same month and year
                return `${start.format("MMM YYYY")}`;
            } else {
                // Different months, same year
                return `${start.format("MMM")} - ${end.format("MMM YYYY")}`;
            }
        } else {
            // Different years
            return `${start.format("MMM YYYY")} - ${end.format("MMM YYYY")}`;
        }
    }

    const getCircleDot = (value) => {
        console.log(value)
        let barColor, color;
        switch (value) {
            case 5:
                barColor = "rgba(0, 100, 0, 1)"; // Dark Green
                color = "#fff";
                break;
            case 4:
                barColor = "rgba(168, 230, 207, 1)"; // Tea-green
                color = "#fff";
                break;
            case 3:
                barColor = "rgba(176, 176, 176, 1)"; // Neutral Grey
                color = "#fff";
                break;
            case 2:
                barColor = "rgba(255, 193, 7, 1)"; // Amber
                color = "#fff";
                break;
            case 1:
                barColor = "rgba(229, 57, 53, 1)"; // Red
                color = "#fff";
                break;
            case 0:
            default:
                barColor = "rgba(176, 176, 176, 1)";
                color = "#fff";
        }

        return <Box style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: barColor,
            color: color,
            fontWeight: "normal",
            fontSize: "0.9rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "normal",
            fontSize: "0.9rem",
            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
        }}
        >
            {value}
        </Box>
    }

    const getSliderLinearProgress = (value) => {
        // Color mapping for bar
        let barColor, bgColor;
        switch (value) {
            case 5:
                barColor = "rgba(0, 100, 0, 1)"; // Dark Green
                bgColor = "rgba(0, 100, 0, 0.2)";
                break;
            case 4:
                barColor = "rgb(124, 179, 66)"; // Tea-green
                bgColor = "rgba(124, 179, 66, 0.2)";
                break;
            case 3:
                barColor = "rgb(251, 192, 45)"; // Neutral Grey
                bgColor = "rgba(251, 192, 45, 0.2)";
                break;
            case 2:
                barColor = "rgb(255, 124, 0)"; // Amber
                bgColor = "rgba(255, 124, 0, 0.2)";
                break;
            case 1:
                barColor = "rgb(211, 47, 47)"; // Red
                bgColor = "rgba(211, 47, 47, 0.2)";
                break;
            default:
                barColor = "rgba(176, 176, 176, 1)";
                bgColor = "rgba(176, 176, 176, 0.2)";
        }
        return (
            <Box sx={{ position: "relative", width: "100%", maxWidth: "130px", margin: "0 auto" }}>
                <LinearProgress
                    variant="determinate"
                    min={1}
                    max={5}
                    value={(value / 5) * 100}
                    sx={{
                        height: 5,
                        borderRadius: 5,
                        backgroundColor: bgColor, // use your bgColor logic
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: barColor, // use your barColor logic
                        }, maxWidth: "125px"
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        top: -9,
                        left: `calc(${(value / 5) * 100}% - 14px)`,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: barColor,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "normal",
                        fontSize: "0.9rem",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                    }}
                >
                    {value}
                </Box>
            </Box>
        );
    }

    function renderQolLegend() {
        const items = [
            { value: 5, color: "rgba(0, 100, 0, 1)", label: "Very Good" },
            { value: 4, color: "#7CB342", label: "Good" },
            { value: 3, color: "#FBC02D", label: "Neutral" },
            { value: 2, color: "#F57C00", label: "Bad" },
            { value: 1, color: "#D32F2F", label: "Very Bad" },
        ];
        return (
            <div style={{ display: "flex", flexDirection: "row", gap: 20 }}>
                {items.map(item => (
                    <div key={item.value} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                        <div style={{
                            background: item.color,
                            color: "#fff",
                            borderRadius: "50%",
                            width: 24,
                            height: 24,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 500
                        }}>{item.value}</div>
                        <span style={{ fontSize: 12 }}>{item.label}</span>
                    </div>
                ))}
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                    <div style={{
                        background: '#B0B0B0',
                        color: "#fff",
                        borderRadius: "50%",
                        width: 24,
                        height: 24,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.6rem",
                        fontWeight: 500
                    }}>N/A</div>
                    <span style={{ fontSize: "0.8rem" }}>Not Completed</span>
                </div>
            </div>
        );
    }

    return <Box mt={3}>
        <Box display="flex" alignItems="center"  >
            <Typography sx={dashboardText} variant="h6" component="div" marginLeft={1}>
                Wellbeing & Quality of Life Overview
            </Typography>
        </Box>
        <Box m={2} />
        <Box sx={mainGrid}>
            <Box my={2} display="flex" justifyContent="flex-start">
                <Typography
                    sx={{ ...dashboardText, fontSize: "1.4rem", fontWeight: "normal" }}
                    component="div"
                    marginLeft={1}>
                    Trend over Last 6 Submissions ({getMonthYearRange(startDate, endDate)})
                </Typography>
                {loadingReportData ? <Box mx={2}><CircularProgress size="2rem" mt={2} /></Box> : null}
                <Box ml="auto">
                    <Box display="flex" alignItems="center">
                        {reportData && reportData.length > 0 && (
                            <Button variant="contained" onClick={handleDownloadDocx} sx={{ ...tbg, ...dateDirBtn, mr: 6, alignSelf: 'center' }}>
                                Download (DOCX)
                            </Button>
                        )}
                        <ButtonGroup disabled={loadingReportData} variant="contained" aria-label="Basic button group" sx={tbg}>
                            <Button sx={dateDirBtn} onClick={() => { handleDateRangeChange(DateRangeChangeDirection.Left) }}><KeyboardArrowLeftOutlinedIcon /></Button>
                            <Button sx={dateDirBtn} onClick={() => { handleDateRangeChange(DateRangeChangeDirection.Right) }}><KeyboardArrowRightOutlinedIcon /></Button>
                            <Button sx={dateDirBtn} onClick={() => { handleDateRangeChange(DateRangeChangeDirection.Today) }}>TODAY</Button>
                        </ButtonGroup>
                    </Box>
                </Box>
            </Box>
            {errorReportData !== '' ? <Box py={2}>
                <Typography style={{ color: "red" }}>{errorReportData}</Typography>
            </Box> : null}
            {reportData != null ? <>
                <Box paddingBottom={4}>{renderQolLegend()}</Box>
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="simple table">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#E4F2F2" }}>
                                <TableCell sx={tableHeaderCell}>Submitted On</TableCell>
                                {reportData.map((point) => {
                                    return <TableCell align="center" key={point.rangeDisplay} sx={{ ...tableHeaderCell, width: getTableCellWidth(display) }}>{dayjs(point.answerSubmitDate).format('DD MMM')}</TableCell>;
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: rowBgColor } }}>
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Able to attend school?
                                </TableCell>
                                {reportData.map((point, index) => {
                                    if (point.attendSchool == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}>N/A</TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            <StatusDot value={point.attendSchool} />
                                            {point.attendSchool === "Yes" && point.daysAttended != null ? <Typography variant="body2" mt={1}>{`(${point.daysAttended}/5 days)`}</Typography> : null}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: rowBgColor } }}>
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Participate in family life?
                                </TableCell>
                                {reportData.map((point, index) => {
                                    if (point.participateFamilyLife == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}>N/A</TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            <StatusDot value={point.participateFamilyLife} />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: rowBgColor } }}>
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Enjoy family activities?
                                </TableCell>
                                {reportData.map((point, index) => {
                                    if (point.enjoyFamilyActivities == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}>N/A</TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            <StatusDot value={point.enjoyFamilyActivities} />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{ backgroundColor: "#E3E7E8", height: 5 }}>
                                <TableCell colSpan={reportData.length + 1} sx={{ padding: 0, border: "none", height: 5 }} />
                            </TableRow>
                            <TableRow sx={{ height: 20 }}>
                                <TableCell colSpan={reportData.length + 1} sx={{ padding: 0, border: "none", height: 5 }} />
                            </TableRow>
                            <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: rowBgColor } }}>
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Overall Week
                                </TableCell>
                                {reportData.map((point, index) => {
                                    if (point.overAllWeekScore == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}>N/A</TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.overAllWeekScore)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: rowBgColor } }}>
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>Seizures</TableCell>
                                {reportData.map((point, index) => {
                                    if (point.seizures == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}>N/A</TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.seizures)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{ "&:nth-of-type(odd)": { backgroundColor: rowBgColor } }}>
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Alertness
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.alertness == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.alertness)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Interaction
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.interaction == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.interaction)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Memory & Concentration
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.memoryAndConcentration == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.memoryAndConcentration)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Understanding & Communication
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.understanding == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.understanding)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Behaviour
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.behaviour == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.behaviour)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Sleep
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.sleep == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.sleep)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    General Wellbeing
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.generalWellbeing == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.generalWellbeing)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Family Wellbeing
                                </TableCell>
                                {reportData.map((point, index) => {
                                    // Show a colored range slider for overAllWeekScore (1-5)
                                    if (point.familyWellbeing == null) {
                                        return <TableCell align="center" key={index} sx={tableBodyCell}></TableCell>;
                                    }
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            {getSliderLinearProgress(point.familyWellbeing)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Other Contributing factors
                                </TableCell>
                                {reportData.map((point, index) => {
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>

                                            {point.factorsAffectingWeek && point.factorsAffectingWeek.length > 0
                                                ? <Typography sx={{ fontWeight: "normal", fontSize: "0.96rem" }}>{point.factorsAffectingWeek.join(', ')}</Typography>
                                                : <Typography sx={{ fontWeight: "normal", fontSize: "0.865rem", color: "grey" }} >Not Answered</Typography>}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow sx={{
                                "&:nth-of-type(odd)": {
                                    backgroundColor: rowBgColor,
                                },
                            }}
                            >
                                <TableCell component="th" scope="row" sx={tableBodyFirstCell}>
                                    Completed Form
                                </TableCell>
                                {reportData.map((point, index) => {
                                    return (
                                        <TableCell align="center" key={index} sx={tableBodyCell}>
                                            <PatientFormDialog formId={point.formId} dialogTitle={"Form Submitted on " + dayjs(point.answerSubmitDate).format('DD/MMM/YY')} />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                {/* Show summary values below the table */}
                {/* {typeof reportData === 'object' && (schoolAttendanceSummary || familyParticipationSummary || enjoyFamilyActivitiesSummary || avgDaysAttended !== undefined) ? (
                    <Box mt={3} p={2} bgcolor="#F9F9F9" borderRadius={2}>
                        <Typography style={{ marginBottom: "10px", fontWeight: "bold" }}>Quick Summary ({getMonthYearRange(startDate, endDate)}):</Typography>
                        <TableContainer component={Paper} style={{ maxWidth: 700, marginTop: 8, marginBottom: 8 }}>
                            <Table size="small" aria-label="summary table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{ fontWeight: 'bold', fontSize: 13 }}>Metric</TableCell>
                                        <TableCell align="center" style={{ fontWeight: 'bold', fontSize: 13 }}>Yes</TableCell>
                                        <TableCell align="center" style={{ fontWeight: 'bold', fontSize: 13 }}>No</TableCell>
                                        <TableCell align="center" style={{ fontWeight: 'bold', fontSize: 13 }}>NA</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>School Attendance</TableCell>
                                        <TableCell align="center">{schoolAttendanceSummary['Yes']}</TableCell>
                                        <TableCell align="center">{schoolAttendanceSummary['No']}</TableCell>
                                        <TableCell align="center">{schoolAttendanceSummary['NA']}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Average Days Attended</TableCell>
                                        <TableCell align="center" colSpan={3}>{avgDaysAttended !== undefined ? Math.round(avgDaysAttended)   : '-'}</TableCell>
                                        
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Family Participation</TableCell>
                                        <TableCell align="center">{familyParticipationSummary['Yes']}</TableCell>
                                        <TableCell align="center">{familyParticipationSummary['No']}</TableCell>
                                        <TableCell align="center">{familyParticipationSummary['NA']}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>Enjoy Family Activities</TableCell>
                                        <TableCell align="center">{enjoyFamilyActivitiesSummary['Yes']}</TableCell>
                                        <TableCell align="center">{enjoyFamilyActivitiesSummary['No']}</TableCell>
                                        <TableCell align="center">{enjoyFamilyActivitiesSummary['NA']}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell></TableCell> 
                                        <TableCell align="center" sx={{fontStyle: 'italic'}} colSpan={3}>Numbers indicate response counts</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                ) : null} */}
            </>
                : null}
        </Box>
    </Box>;
}

const StatusDot = ({ value }) => {
    let color;
    switch (value) {
        case 'Yes':
            color = '#4CAF50'; // Green
            break;
        case 'No':
            color = '#F44336'; // Red
            break;
        case 'N/A':
        default:
            color = '#B0B0B0'; // Grey
            break;
    }

    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            {/* <Box
                sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: color,
                }}
            /> */}
            {value === 'Yes' ? <DoneIcon style={{ color: color }} /> : <CloseIcon style={{ color: color }} />}
            <Typography variant="body2" mt={1}>{value}</Typography>
        </Box>
    );
};

const CollapsibleListDialog = ({ items }) => {
    return (
        <>
            {items.length > 1 ?
                <select style={{ padding: "8px", fontSize: "0.9rem", color: "#26575C", backgroundColor: "transparent", border: "0px" }}>
                    <option selected>{items.length} Factors</option>
                    {items.map((item, idx) => (
                        <option key={idx} >{item}</option>
                    ))}
                </select> : <Typography component="div"
                    style={{ fontWeight: "normal", fontSize: "0.9rem", color: "#26575C" }}>{items[0]}</Typography>}
        </>
    );
};